import React from 'react';
import styles from './Footer.module.css';
import { FaFacebook, FaGithub, FaInstagram, FaEnvelope, FaPhone } from 'react-icons/fa';
import { CiLinkedin } from 'react-icons/ci';
import { useTranslation } from 'react-i18next';

const smoothScrollPolyfill = (offsetPosition: number, duration: number) => {
  window.scrollTo({
    top: offsetPosition,
    behavior: 'smooth',
  });
};

interface FooterProps {
  sections: Array<{ id: string, label: string }>;
  children?: React.ReactNode;
}

const Footer: React.FC<FooterProps> = ({ sections }) => {
  const { t, i18n } = useTranslation();

  const handleScroll = (id: string) => {
    const element = document.getElementById(id);
    if (element) {
      const elementPosition = element.getBoundingClientRect().top + window.scrollY;
      const offsetPosition = elementPosition - 100;
      smoothScrollPolyfill(offsetPosition, 1000);
    }
  };

  const handleLanguageChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    i18n.changeLanguage(e.target.value);
  };

  return (
    <footer className={styles.footer} dir={i18n.language === 'ar' ? 'rtl' : 'ltr'}>
      <div className={styles.container}>
        {/* Kontakt Info */}
        <div className={styles.section}>
          <h5 className={styles.heading}>{t('footer.contactInfo')}</h5>
          <p className={styles.text}>{t('footer.contactText')}</p>
          <ul className={styles.noBullets}>
            <li className={styles.contactItem}>
              <FaEnvelope className={styles.icon} />
              <a href="mailto:ki.yazan.1@hotmail.com" className={styles.link}>
              ki.yazan.1@hotmail.com
              </a>
            </li>
            <li className={styles.contactItem}>
              <FaPhone className={styles.icon} />
              <a href="tel:+491738549626" className={styles.link}>
              00491738549626
              </a>
            </li>
          </ul>
        </div>

        {/* Quick Links */}
        <div className={styles.section}>
          <h5 className={styles.heading}>{t('footer.sections')}</h5>
          <ul className={styles.noBullets}>
            {sections.map((section) => (
              <li key={section.id}>
                <a
                  href={`#${section.id}`}
                  className={styles.link}
                  onClick={(e) => {
                    e.preventDefault();
                    handleScroll(section.id);
                  }}
                >
                  {section.label}
                </a>
              </li>
            ))}
          </ul>
        </div>

        {/* Folge uns */}
        <div className={styles.section}>
          <h5 className={styles.heading}>{t('footer.followUs')}</h5>
          <div className={styles.socialIcons}>
            <a href="#" className={styles.socialIcon}><FaFacebook /></a>
            <a href="#" className={styles.socialIcon}><FaInstagram /></a>
            <a href="#" className={styles.socialIcon}><FaGithub /></a>
            <a href="#" className={styles.socialIcon}><CiLinkedin /></a>
          </div>
        </div>

        {/* Language Selector */}
        <div className={styles.section}>
          <h5 className={styles.heading}>{t('footer.language')}</h5>
          <select onChange={handleLanguageChange} value={i18n.language} className={styles.languageSelector}>
            <option value="de">Deutsch</option>
            <option value="en">English</option>
            <option value="ar">العربية</option>
          </select>
        </div>
      </div>

      <hr className={styles.separator} />
      <div className={styles.copyRight}>
        <p>{t('footer.copyRight')}</p>
      </div>
    </footer>
  );
};

export default Footer;
